import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bell,
  Mail,
  MessageCircle,
  MessageSquareText,
  Sparkles,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  COMMUNICATION_CHANNEL_KINDS,
  CommunicationChannelKind,
} from "@/types/messaging-channel-config";

const AUTO = "AUTO" as const;
type SelectorValue = CommunicationChannelKind | typeof AUTO;

interface GuestChannelPreferenceSelectorProps {
  preferred: CommunicationChannelKind | null;
  enabledChannels: CommunicationChannelKind[];
  allChannelsOnMatrix: CommunicationChannelKind[];
  disabled: boolean;
  onChange: (next: SelectorValue) => void;
}

const CHANNEL_ICON: Record<CommunicationChannelKind, React.ReactNode> = {
  IN_APP: <Bell className="text-skyline-2 size-4" />,
  EMAIL: <Mail className="text-skyline-2 size-4" />,
  SMS: <MessageSquareText className="text-skyline-2 size-4" />,
  WHATSAPP: <MessageCircle className="text-skyline-2 size-4" />,
};

export const GuestChannelPreferenceSelector: FC<
  GuestChannelPreferenceSelectorProps
> = ({
  preferred,
  enabledChannels,
  allChannelsOnMatrix,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();
  const value: SelectorValue = preferred ?? AUTO;

  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(v) => onChange(v as SelectorValue)}
    >
      <SelectTrigger
        aria-label={t("communicationChannels.guestPreference.selectAria")}
        className="w-full min-w-0 justify-between [&>span]:min-w-0 [&>span]:truncate"
      >
        <SelectValue
          placeholder={t(
            "communicationChannels.guestPreference.selectPlaceholder",
          )}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={AUTO}>
          <span className="inline-flex items-center gap-2">
            <Sparkles className="text-skyline-2 size-4" aria-hidden />
            {t("communicationChannels.guestPreference.options.auto")}
          </span>
        </SelectItem>

        {COMMUNICATION_CHANNEL_KINDS.filter((channel) =>
          allChannelsOnMatrix.includes(channel),
        ).map((channel) => {
          const isUsable = enabledChannels.includes(channel);
          const item = (
            <SelectItem key={channel} value={channel} disabled={!isUsable}>
              <span className="inline-flex items-center gap-2">
                {CHANNEL_ICON[channel]}
                {t(`communicationChannels.channels.${channel}.label`)}
              </span>
            </SelectItem>
          );
          if (isUsable) return item;
          return (
            <TooltipProvider key={channel} delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex w-full">{item}</span>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  {t("communicationChannels.guestPreference.optionDisabled", {
                    channel: t(
                      `communicationChannels.channels.${channel}.label`,
                    ),
                  })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </SelectContent>
    </Select>
  );
};
